import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { FOCUS_RING_BRAND_SHADOW_SM } from '../styles/common';
import { cn } from '../utils';

const innerBorderGradient =
  'before:border-[rgba(255,255,255,0.12)] before:mask-b-from-0% before:absolute before:inset-px before:border before:rounded-[7px]';

const buttonVariants = cva(
  cn(
    'relative inline-flex select-none items-center justify-center whitespace-nowrap font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50',
    FOCUS_RING_BRAND_SHADOW_SM,
  ),
  {
    variants: {
      variant: {
        default:
          // Colors don't change in dark theme
          cn('bg-brand-600 text-white hover:bg-brand-700', innerBorderGradient),
        destructive: cn(
          'bg-red-500 text-red-50 hover:bg-red-500/90',
          innerBorderGradient,
        ),
        outline:
          'text-secondary border border-primary bg-primary hover:bg-primary-hover hover:text-secondary-hover focus-visible:border-brand-500 shadow-xs',
        secondary: 'bg-tertiary text-primary hover:bg-tertiary/80',
        ghost: 'hover:bg-secondary-hover hover:text-secondary-hover',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        xs: 'px-2 py-1 text-xs gap-1 rounded-sm',
        sm: 'px-3 py-2 text-sm gap-1.5 rounded-md',
        default: 'px-3.5 py-2.5 text-sm gap-1.5 rounded-md',
        lg: 'px-4 py-2.5 text-md gap-2 rounded-md',
        icon: 'size-10 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...{
          ...(Comp === 'button' && {
            type: 'button',
          }),
          ...props,
        }}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
